import React, { useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import {
  ArrowRight, Sun, Moon, Bus, Calculator, Home, Briefcase, Rocket,
  Palette, Sparkles, DoorOpen, CheckCircle2, Users, GraduationCap, Wrench,
} from "lucide-react";

// Master service definitions. Add/edit here — no admin toggle needed for Phase 1.
const SERVICES = [
  { id: "after_school", title: "After-School Care", icon: Sun,
    lead: "math_science_tutoring", comingSoon: false,
    desc: "A structured, supportive environment for children after school — supervised care with homework support, reading, educational activities and age-appropriate enrichment.",
    features: [
      "Safe, structured after-school environment",
      "Homework and study support",
      "Reading and educational activities",
      "Optional Math & Science tutoring",
      "STEM and enrichment activities",
      "Flexible program options",
    ],
    fine: "Subject to licensing, program availability and applicable requirements.",
    cta: "Enquire About After-School Care", service: "after_school" },
  { id: "before_school", title: "Before-School Care", icon: Moon,
    comingSoon: false,
    desc: "A convenient morning program to help families manage busy school-day schedules. Children start their day in a supervised environment with quiet learning and school-day prep.",
    features: [
      "Morning supervision", "Quiet learning activities",
      "Reading and educational activities", "School-day preparation",
      "Convenient options for working families",
    ],
    fine: "Subject to licensing, program availability and applicable requirements.",
    cta: "Enquire About Before-School Care", service: "before_school" },
  { id: "school_pickup", title: "School Pickup & Transportation", icon: Bus,
    comingSoon: false,
    desc: "Planned transportation options for eligible students in selected programs. Where available, students are picked up from participating schools and transported to the Teacher4Me centre.",
    features: [
      "Selected school pickup", "Transportation to Teacher4Me",
      "Convenient for working parents", "Complements after-school programs",
      "Scheduled routes based on demand",
    ],
    fine: "Availability depends on school location, route capacity, enrolment, insurance and applicable regulatory requirements.",
    cta: "Join Transportation Interest List", service: "school_pickup" },
  { id: "math_science_tutoring", title: "Math & Science Tutoring", icon: Calculator,
    comingSoon: false, primaryCta: "Book Free Assessment", primaryHref: "/free-assessment",
    desc: "Personalised academic support focused on understanding concepts — not just completing homework. In-person at our NW Calgary centre or online.",
    features: [
      "Math tutoring (K-12)", "Science tutoring (K-12)",
      "Homework support", "Concept reinforcement",
      "Test and exam preparation",
      "Individual and small-group options",
      "In-person and online options",
    ],
    cta: "Enquire About Tutoring", service: "math_science_tutoring" },
  { id: "homeschool_hub", title: "Homeschool Learning Hub", icon: Home,
    comingSoon: true,
    desc: "A flexible daytime learning hub in development for homeschooling families — Math, Science, English, STEM, project-based learning, study support and small-group activities.",
    features: [
      "Daytime academic support", "Math, Science and English",
      "STEM activities", "Project-based learning",
      "Small-group learning", "Flexible scheduling",
    ],
    fine: "Teacher4Me's proposed Homeschool Learning Hub is intended to provide supplementary educational support and enrichment. Teacher4Me is not an Alberta school authority or supervising home-education authority; parents remain responsible for meeting applicable home-education requirements.",
    cta: "Join the Interest List", service: "homeschool_hub" },
  { id: "career_courses", title: "Career & Professional Development Courses", icon: Briefcase,
    comingSoon: false, primaryCta: "Explore Courses", primaryHref: "/courses",
    desc: "Practical, career-focused learning for adults, newcomers, students and working professionals. Structured online and instructor-supported courses.",
    features: [
      "Early Childhood Care & Education Foundations",
      "Education Assistant Foundations",
      "Office Administration", "Customer Service",
      "Business & Entrepreneurship", "Digital Skills",
      "Microsoft Office", "AI & Productivity",
      "Communication & Professional Development",
    ],
    cta: "Enquire About Courses", service: "career_courses" },
  { id: "stem_ai_clubs", title: "Weekend STEM & AI Clubs", icon: Rocket,
    comingSoon: true,
    desc: "Interactive weekend programs that introduce young learners to technology, problem-solving and the changing world of artificial intelligence — learning by doing.",
    features: [
      "Beginner AI concepts", "Coding activities",
      "Science projects", "STEM challenges",
      "Creative technology", "Team problem-solving",
    ],
    fine: "Programs will be offered based on age group, instructor availability and minimum enrolment.",
    cta: "Join the Interest List", service: "stem_ai_clubs" },
  { id: "pd_day_camps", title: "PD Day & Spring Break Programs", icon: Palette,
    comingSoon: true,
    desc: "Structured educational and recreational programs for school PD days and school breaks — a balanced schedule of learning, creativity, activities and social interaction.",
    features: [
      "STEM activities", "Science experiments",
      "Math games", "Creative projects",
      "AI and technology activities", "Reading and study support",
    ],
    cta: "Join Interest List", service: "pd_day_camps" },
  { id: "summer_camps", title: "Summer Learning Camps", icon: Sparkles,
    comingSoon: true,
    desc: "Keep children active, curious and learning during summer. Planned programs combine educational enrichment with enjoyable group activities.",
    features: [
      "STEM Camp", "Math Challenge Camp",
      "Science Discovery", "AI & Technology",
      "Reading & Communication", "Academic Catch-Up",
      "Back-to-School Preparation",
    ],
    fine: "Program dates, age groups, pricing and availability will be announced separately.",
    cta: "Join Summer Interest List", service: "summer_camps" },
  { id: "room_rental", title: "Teaching Space & Instructor Rental", icon: DoorOpen,
    comingSoon: false,
    desc: "Selected classrooms and teaching spaces are available to qualified educators, tutors, trainers and educational organisations during available hours.",
    features: [
      "Professional learning environment",
      "Hourly or scheduled arrangements",
      "Evening / weekend availability",
      "Suitable for small classes and workshops",
    ],
    fine: "Availability and permitted activities are subject to Teacher4Me approval, building rules, insurance requirements and applicable regulations.",
    cta: "Enquire About Space", service: "room_rental" },
];

const PERSONAS = [
  { icon: Users, title: "I need childcare",
    picks: ["Before-School Care", "After-School Care", "School Pickup", "Camps"] },
  { icon: GraduationCap, title: "My child needs academic help",
    picks: ["Math & Science Tutoring", "Homework Support", "Homeschool Learning Hub"] },
  { icon: Sparkles, title: "I want enrichment for my child",
    picks: ["STEM & AI Clubs", "Camps", "Educational Activities"] },
  { icon: Briefcase, title: "I want to develop career skills",
    picks: ["Career Courses", "Professional Development", "Online Learning"] },
  { icon: Wrench, title: "I'm an instructor / trainer",
    picks: ["Teaching Space Rental", "Collaboration Opportunities"] },
];

export default function ServicesPage() {
  useSeo({
    title: "Services — Learning, Care & Skills Development | Teacher4Me",
    description:
      "Tutoring, before and after-school care, STEM clubs, career courses, homeschool support and summer camps — Teacher4Me is a complete learning and development destination for children, families and adult learners.",
    canonical: "https://teacher4me.ca/services",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "ItemList",
          "@id": "https://teacher4me.ca/services#list",
          "name": "Teacher4Me Services",
          "itemListElement": SERVICES.map((s, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "item": {
              "@type": "Service",
              "name": s.title,
              "description": s.desc,
              "provider": {"@type": "EducationalOrganization", "name": "Teacher4Me"},
              "areaServed": {"@type": "AdministrativeArea", "name": "Calgary, Alberta"},
              "availability": s.comingSoon
                ? "https://schema.org/PreOrder"
                : "https://schema.org/InStock",
            },
          })),
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            {"@type": "ListItem", "position": 1, "name": "Teacher4Me", "item": "https://teacher4me.ca"},
            {"@type": "ListItem", "position": 2, "name": "Services", "item": "https://teacher4me.ca/services"},
          ],
        },
      ],
    },
  });

  const [initialService, setInitialService] = useState("");

  const jumpToEnquiry = (serviceId) => {
    setInitialService(serviceId);
    setTimeout(() => {
      document.getElementById("enquiry-form")?.scrollIntoView({ behavior: "smooth" });
    }, 30);
  };

  return (
    <div>
      {/* HERO */}
      <section className="bg-paper border-b border-[#E5E7EB]">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="label-eyebrow">Learning, Care & Skills Development — All in One Place</div>
          <h1 className="font-display font-black text-4xl md:text-5xl mt-3 leading-tight tracking-tight max-w-3xl">
            More Than Tutoring.<br />A Complete <span className="text-[#D92D20]">Learning & Development</span> Centre.
          </h1>
          <p className="mt-5 text-[#4B5563] max-w-2xl leading-relaxed">
            Teacher4Me supports children, students, families and adult learners through academic
            tutoring, before and after-school programs, enrichment activities, career-focused
            learning and flexible educational services.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <a href="#services-grid" className="btn-primary flex items-center gap-2" data-testid="hero-explore">
              Explore Services <ArrowRight className="w-4 h-4" />
            </a>
            <Link to="/contact" className="btn-outline">Contact Us</Link>
          </div>
          <p className="mt-6 text-xs text-[#4B5563] max-w-2xl">
            Programs and availability may vary. Certain childcare, transportation and specialised
            programs are subject to applicable licensing, approvals, staffing and minimum enrolment
            requirements.
          </p>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section id="services-grid" className="max-w-6xl mx-auto px-6 py-14">
        <div className="label-eyebrow">Our services</div>
        <h2 className="font-display font-black text-3xl md:text-4xl mt-1 mb-8">Everything Teacher4Me offers.</h2>
        <div className="grid md:grid-cols-2 gap-5">
          {SERVICES.map((s) => (
            <ServiceCard key={s.id} s={s} onEnquire={() => jumpToEnquiry(s.service)} />
          ))}
        </div>
      </section>

      {/* PERSONAS */}
      <section className="bg-white border-y border-[#E5E7EB]">
        <div className="max-w-6xl mx-auto px-6 py-14">
          <div className="label-eyebrow">Find the right program</div>
          <h2 className="font-display font-black text-3xl md:text-4xl mt-1 mb-8">What are you looking for?</h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-5">
            {PERSONAS.map(({ icon: Icon, title, picks }) => (
              <div key={title} className="bg-white border border-[#E5E7EB] rounded-2xl p-5"
                data-testid={`persona-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <Icon className="w-6 h-6 text-[#D92D20]" />
                <div className="font-display font-bold text-lg mt-3">{title}</div>
                <ul className="mt-3 space-y-1.5 text-sm text-[#4B5563]">
                  {picks.map((p) => (
                    <li key={p} className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#064E3B] mt-2 shrink-0" />
                      {p}
                    </li>
                  ))}
                </ul>
                <a href="#enquiry-form" className="text-xs font-semibold text-[#D92D20] mt-4 inline-block hover:underline">
                  Send an enquiry →
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ENQUIRY */}
      <section id="enquiry-form" className="max-w-3xl mx-auto px-6 py-14">
        <div className="label-eyebrow">Tell us what you're looking for</div>
        <h2 className="font-display font-black text-3xl md:text-4xl mt-1 mb-6">Send us an enquiry.</h2>
        <EnquiryForm initialService={initialService} />
      </section>
    </div>
  );
}

// ---------- Service card ----------
function ServiceCard({ s, onEnquire }) {
  const [open, setOpen] = useState(false);
  const Icon = s.icon;
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 flex flex-col"
      data-testid={`service-card-${s.id}`}>
      <div className="flex items-start justify-between gap-3">
        <Icon className="w-7 h-7 text-[#D92D20]" />
        {s.comingSoon && (
          <span className="text-[10px] font-bold uppercase tracking-widest bg-[#FFFBEB] text-[#92400E] border border-[#FDE68A] rounded-full px-2 py-1">
            Coming Soon
          </span>
        )}
      </div>
      <h3 className="font-display font-bold text-xl mt-3">{s.title}</h3>
      <p className="text-sm text-[#4B5563] mt-2 leading-relaxed">{s.desc}</p>

      {open && (
        <ul className="mt-4 space-y-1.5 text-sm">
          {s.features.map((f) => (
            <li key={f} className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#064E3B] mt-0.5 shrink-0" />
              <span>{f}</span>
            </li>
          ))}
          {s.fine && (
            <li className="text-xs text-[#4B5563] italic pt-2">{s.fine}</li>
          )}
        </ul>
      )}

      <div className="mt-auto pt-4 flex flex-wrap items-center gap-3">
        {s.primaryCta && s.primaryHref ? (
          <Link to={s.primaryHref} className="btn-primary text-xs px-4 py-2"
            data-testid={`primary-${s.id}`}>
            {s.primaryCta}
          </Link>
        ) : null}
        <button onClick={onEnquire}
          data-testid={`enquire-${s.id}`}
          className="text-xs px-4 py-2 border border-[#E5E7EB] rounded-md font-semibold hover:border-[#D92D20]">
          {s.cta}
        </button>
        <button onClick={() => setOpen((v) => !v)}
          data-testid={`more-${s.id}`}
          className="text-xs font-semibold text-[#D92D20] hover:underline ml-auto">
          {open ? "Show less" : "Learn more →"}
        </button>
      </div>
    </div>
  );
}

// ---------- Enquiry form (reuses /api/centre/leads) ----------
const SERVICE_OPTIONS = [
  { v: "math_science_tutoring", l: "Math & Science Tutoring" },
  { v: "after_school", l: "After-School Care" },
  { v: "before_school", l: "Before-School Care" },
  { v: "school_pickup", l: "School Pickup / Transportation" },
  { v: "homeschool_hub", l: "Homeschool Learning Hub" },
  { v: "career_courses", l: "Career / CPD Courses" },
  { v: "stem_ai_clubs", l: "Weekend STEM / AI Clubs" },
  { v: "pd_day_camps", l: "PD / Spring Break Programs" },
  { v: "summer_camps", l: "Summer Camps" },
  { v: "room_rental", l: "Room / Instructor Rental" },
  { v: "other", l: "Other" },
];

function EnquiryForm({ initialService }) {
  const [parent, setParent] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [studentName, setStudentName] = useState("");
  const [grade, setGrade] = useState("");
  const [service, setService] = useState(initialService || "math_science_tutoring");
  const [start, setStart] = useState("");
  const [days, setDays] = useState("");
  const [info, setInfo] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  React.useEffect(() => {
    if (initialService) setService(initialService);
  }, [initialService]);

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) return toast.error("Please tick the consent checkbox to continue.");
    setBusy(true);
    try {
      await api.post("/api/centre/leads", {
        parent_name: parent, student_name: studentName, phone, email,
        grade, subject: "other", service,
        concern: info, additional_info: info,
        preferred_contact: "phone",
        preferred_start: start, preferred_days: days,
        consent: true, source: "services_page",
      });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="bg-[#ECFDF5] border border-[#A7F3D0] rounded-2xl p-8 text-center"
        data-testid="services-enquiry-done">
        <CheckCircle2 className="w-12 h-12 mx-auto text-[#064E3B]" />
        <h3 className="font-display font-black text-2xl mt-3">Enquiry sent.</h3>
        <p className="text-sm text-[#065F46] mt-2">
          Thank you — a Teacher4Me team member will be in touch shortly.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white border border-[#E5E7EB] rounded-2xl p-6 space-y-4"
      data-testid="services-enquiry-form">
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Your name*" value={parent} setValue={setParent} testid="s-parent" required />
        <Field label="Email*" value={email} setValue={setEmail} testid="s-email" type="email" required />
        <Field label="Phone*" value={phone} setValue={setPhone} testid="s-phone" type="tel" required />
        <div>
          <label className="text-sm font-semibold">Service interested in*</label>
          <select data-testid="s-service" value={service} onChange={(e) => setService(e.target.value)}
            className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm bg-white">
            {SERVICE_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        </div>
        <Field label="Student name (optional)" value={studentName} setValue={setStudentName} testid="s-student" />
        <Field label="Student age / grade (optional)" value={grade} setValue={setGrade} testid="s-grade" />
        <Field label="Preferred start date" value={start} setValue={setStart} testid="s-start" type="date" />
        <Field label="Preferred days" value={days} setValue={setDays} testid="s-days"
          placeholder="e.g. Mon/Wed/Fri or Weekends" />
      </div>
      <div>
        <label className="text-sm font-semibold">Additional information</label>
        <textarea data-testid="s-info" rows={3} value={info}
          onChange={(e) => setInfo(e.target.value)}
          className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#D92D20]" />
      </div>
      <label className="flex items-start gap-2 text-sm text-[#4B5563]">
        <input type="checkbox" data-testid="s-consent" checked={consent}
          onChange={(e) => setConsent(e.target.checked)} className="mt-1" />
        I agree to be contacted by Teacher4Me regarding my enquiry.
      </label>
      <button type="submit" disabled={busy} data-testid="s-submit" className="btn-primary">
        {busy ? "Sending…" : "Send Enquiry"}
      </button>
    </form>
  );
}

function Field({ label, value, setValue, testid, type = "text", required, placeholder }) {
  return (
    <div>
      <label className="text-sm font-semibold">{label}</label>
      <input data-testid={testid} type={type} required={required} placeholder={placeholder}
        value={value} onChange={(e) => setValue(e.target.value)}
        className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#D92D20]" />
    </div>
  );
}
